/**
 * Les règles d'un chantier (docs/CADRAGE.md, « Le chantier et sa chaîne d'étapes »).
 *
 * - La chaîne est libre (S10) : des étapes ordonnées, copiées du modèle, qui
 *   peuvent revenir plusieurs fois (réception de matériel). Pas d'ordre imposé
 *   entre elles.
 * - La réception du chantier (`finale`) est la dernière et le clôture : elle
 *   attend la validation de toutes les autres.
 * - Le partenaire envoie son étape au bureau, preuves réunies ; le bureau seul
 *   la valide, ou la refuse avec un motif.
 * - Le bureau peut rouvrir une étape validée, avec un motif.
 *
 * Fonctions pures : l'accès libre et l'écran les appliquent telles quelles, la
 * base les reprend dans db/009-chaine.sql.
 */
import type {
  EtapeChantier,
  Exigences,
  ModeleEtape,
  NatureRefus,
  Refus,
  ResumeEtape,
  StatutEtape,
  TypeDocument,
} from '@/shared/donnees/types';
import { joursEntre } from './conformite';

/** Les documents du catalogue de départ. Le bureau en ajoute d'autres. */
export const DOCUMENTS_DE_DEPART: TypeDocument[] = [
  { id: 'bon-livraison', libelle: 'Bon de livraison' },
  { id: 'pv-reception', libelle: 'PV de réception signé' },
  { id: 'plan-architecte', libelle: 'Plan d’architecte' },
  { id: 'declaration-sous-traitance', libelle: 'Déclaration de sous-traitance' },
  { id: 'pv-levee-reserves', libelle: 'PV de levée de réserves' },
];

/**
 * La chaîne de départ : la visite technique et son relevé, deux réceptions de
 * matériel, et la visite finale.
 */
export const MODELE_PAR_DEFAUT: Omit<ModeleEtape, 'id'>[] = [
  {
    ordre: 1,
    libelle: 'Visite technique',
    metier: 'visite',
    icone: 'controle',
    finale: false,
    exigences: { photosMin: 0, documents: [] },
  },
  {
    ordre: 2,
    libelle: 'Réception de matériel — panneaux solaires',
    metier: 'livraison',
    icone: 'camion',
    finale: false,
    exigences: { photosMin: 1, documents: ['bon-livraison'] },
  },
  {
    ordre: 3,
    libelle: 'Installation solaire',
    metier: 'solaire',
    icone: 'soleil',
    finale: false,
    exigences: { photosMin: 3, documents: [] },
  },
  {
    ordre: 4,
    libelle: 'Aménagement du bâtiment',
    metier: 'batiment',
    icone: 'batiment',
    finale: false,
    exigences: { photosMin: 2, documents: ['plan-architecte'] },
  },
  {
    ordre: 5,
    libelle: 'Réception de matériel — cellule de séchage',
    metier: 'livraison',
    icone: 'colis',
    finale: false,
    exigences: { photosMin: 1, documents: ['bon-livraison'] },
  },
  {
    ordre: 6,
    libelle: 'Installation du système de séchage',
    metier: 'sechage',
    icone: 'ventilateur',
    finale: false,
    exigences: { photosMin: 3, documents: [] },
  },
  {
    ordre: 7,
    libelle: 'Réception du chantier — visite finale',
    metier: 'reception',
    icone: 'drapeau',
    finale: true,
    exigences: { photosMin: 1, documents: ['pv-reception'] },
  },
];

export const LIBELLE_STATUT: Record<StatutEtape, string> = {
  'a-planifier': 'À planifier',
  planifiee: 'Planifiée',
  /* Démarrée par le partenaire ou seulement datée, c'est la même chose pour lui : planifiée. */
  'en-cours': 'Planifiée',
  'a-valider': 'À valider',
  validee: 'Validée',
  rouverte: 'À reprendre',
};

/**
 * Les mêmes statuts, vus du partenaire : une étape qu'il a envoyée n'est pas
 * « à valider » pour lui, elle attend la réponse du bureau.
 */
export const LIBELLE_STATUT_PARTENAIRE: Record<StatutEtape, string> = {
  ...LIBELLE_STATUT,
  'a-valider': 'En attente du bureau',
};

/** Le nom d'un document du catalogue ; son identifiant s'il n'y est plus. */
export function libelleDocument(id: string, catalogue: readonly TypeDocument[]): string {
  if (id === 'photo') return 'Photo';
  if (id === 'autre') return 'Autre document';
  return catalogue.find((d) => d.id === id)?.libelle ?? id;
}

/** Ce qui manque pour valider, en clair. Vide : les preuves sont réunies. */
export function preuvesManquantes(
  exigences: Exigences,
  preuves: readonly { type: string }[],
  catalogue: readonly TypeDocument[]
): string[] {
  const manques: string[] = [];
  const photos = preuves.filter((p) => p.type === 'photo').length;
  if (photos < exigences.photosMin) {
    const reste = exigences.photosMin - photos;
    manques.push(`${reste} photo${reste > 1 ? 's' : ''}`);
  }
  for (const doc of exigences.documents) {
    if (!preuves.some((p) => p.type === doc)) {
      manques.push(libelleDocument(doc, catalogue).toLowerCase());
    }
  }
  return manques;
}

export type Verdict = { ok: true } | { ok: false; raison: string };

type EtapeJugee = Pick<
  EtapeChantier,
  | 'finale'
  | 'statut'
  | 'partenaireId'
  | 'exigences'
  | 'preuves'
  | 'enAttenteDe'
  | 'metier'
  | 'releveComplet'
>;
type Soeur = Pick<EtapeChantier, 'finale' | 'statut' | 'libelle' | 'ordre'>;

/* Ce qui empêche une étape d'être close, dans l'ordre où le dire. */
function obstacle(
  etape: EtapeJugee,
  toutes: readonly Soeur[],
  catalogue: readonly TypeDocument[]
): string | null {
  /* Pour la réception, on dit d'abord ce qu'elle attend : c'est ce qui bloque vraiment. */
  if (etape.finale) {
    const restantes = toutes
      .filter((e) => !e.finale && e.statut !== 'validee')
      .sort((a, b) => a.ordre - b.ordre);
    if (restantes.length > 0) {
      return `La réception du chantier attend la validation de : ${restantes.map((e) => e.libelle.toLowerCase()).join(', ')}.`;
    }
  }
  if (etape.enAttenteDe) {
    return `En attente de : ${etape.enAttenteDe}. Elle doit être validée d’abord.`;
  }
  if (etape.partenaireId === null) return 'Affectez d’abord un partenaire à cette étape.';
  if (etape.metier === 'visite' && !etape.releveComplet) {
    return 'Le relevé de la visite n’est pas complet.';
  }
  const manques = preuvesManquantes(etape.exigences, etape.preuves, catalogue);
  if (manques.length > 0) return `Il manque : ${manques.join(', ')}.`;
  return null;
}

/** Le partenaire peut-il envoyer cette étape au bureau ? */
export function peutSoumettre(
  etape: EtapeJugee,
  toutes: readonly Soeur[],
  catalogue: readonly TypeDocument[]
): Verdict {
  if (etape.statut === 'validee') return { ok: false, raison: 'Cette étape est déjà validée.' };
  if (etape.statut === 'a-valider') {
    return { ok: false, raison: 'Cette étape attend déjà la validation du bureau.' };
  }
  const raison = obstacle(etape, toutes, catalogue);
  return raison ? { ok: false, raison } : { ok: true };
}

/** Le bureau peut-il valider cette étape, au vu des autres étapes du chantier ? */
export function peutValider(
  etape: EtapeJugee,
  toutes: readonly Soeur[],
  catalogue: readonly TypeDocument[]
): Verdict {
  if (etape.statut === 'validee') return { ok: false, raison: 'Cette étape est déjà validée.' };
  const raison = obstacle(etape, toutes, catalogue);
  return raison ? { ok: false, raison } : { ok: true };
}

export function peutRefuser(etape: Pick<EtapeChantier, 'statut'>): Verdict {
  return etape.statut === 'a-valider'
    ? { ok: true }
    : { ok: false, raison: 'Seule une étape envoyée au bureau peut être refusée.' };
}

// ── Le refus motivé ──────────────────────────────────────────────────────────

/**
 * Pourquoi c'est refusé, dans les mots du quotidien : le libellé du menu, et ce
 * que lit le partenaire après « Photos : ». Mêmes valeurs que la base
 * (db/045-motifs-refus.sql).
 */
export const NATURES_REFUS: { id: NatureRefus; libelle: string; phrase: string }[] = [
  { id: 'manquant', libelle: 'Manquant', phrase: 'à fournir' },
  { id: 'incomplet', libelle: 'Incomplet', phrase: 'à compléter' },
  { id: 'illisible', libelle: 'Flou ou illisible', phrase: 'flou ou illisible, à reprendre' },
  { id: 'incoherent', libelle: 'Erroné ou incohérent', phrase: 'erroné ou incohérent, à vérifier' },
  { id: 'mauvaise-qualite', libelle: 'Mauvaise qualité', phrase: 'mauvaise qualité' },
  { id: 'non-conforme', libelle: 'Non conforme', phrase: 'non conforme' },
  { id: 'autre', libelle: 'Autre (préciser dans le commentaire)', phrase: 'voir le commentaire' },
];

/** Le relevé de la visite technique se refuse comme une pièce : « Questionnaire de visite : incomplet ». */
const QUESTIONNAIRE = { id: 'questionnaire', libelle: 'Questionnaire de visite' };

/**
 * Ce qu'on peut refuser sur une étape : ses photos, chacun des documents
 * qu'elle exige, et les travaux eux-mêmes ; pour une visite technique, son
 * questionnaire à la place des travaux. La base n'accepte rien d'autre.
 */
export function objetsRefusables(
  exigences: Exigences,
  catalogue: readonly TypeDocument[],
  visite = false
): { id: string; libelle: string }[] {
  return [
    ...(visite ? [QUESTIONNAIRE] : []),
    { id: 'photos', libelle: 'Photos' },
    ...exigences.documents.map((d) => ({ id: d, libelle: libelleDocument(d, catalogue) })),
    ...(visite ? [] : [{ id: 'travaux', libelle: 'Travaux' }]),
  ];
}

/**
 * Un refus ne part qu'avec au moins un motif complet, sans ligne à moitié
 * remplie ; « Autre » demande un commentaire qui dit quoi.
 */
export function verifierRefus(
  lignes: readonly { objet: string; nature: NatureRefus | '' }[],
  exigences: Exigences,
  visite = false,
  commentaire = ''
): Verdict {
  const permis = new Set([
    'photos',
    ...(visite ? ['questionnaire'] : ['travaux']),
    ...exigences.documents,
  ]);
  if (lignes.length === 0) {
    return { ok: false, raison: 'Choisissez au moins un motif : ce qui est refusé, et pourquoi.' };
  }
  if (lignes.some((l) => l.objet === '' || l.nature === '')) {
    return { ok: false, raison: 'Chaque motif dit ce qui est refusé, et pourquoi.' };
  }
  if (lignes.some((l) => !permis.has(l.objet))) {
    return { ok: false, raison: 'Ce motif ne correspond à rien de ce que l’étape exige.' };
  }
  if (lignes.some((l) => l.nature === 'autre') && commentaire.trim() === '') {
    return { ok: false, raison: 'Pour « Autre », dites en commentaire ce qui est à reprendre.' };
  }
  return { ok: true };
}

/**
 * La phrase que lisent le partenaire et l'e-mail : « Photos : mauvaise qualité ;
 * bon de livraison : non conforme. Reprendre à plat. » Un même motif en double
 * n'est dit qu'une fois.
 */
export function texteRefus(refus: Refus, catalogue: readonly TypeDocument[]): string {
  const nature = (n: NatureRefus) => NATURES_REFUS.find((x) => x.id === n)?.phrase ?? n;
  const objet = (o: string) =>
    o === 'photos'
      ? 'Photos'
      : o === 'travaux'
        ? 'Travaux'
        : o === 'questionnaire'
          ? QUESTIONNAIRE.libelle
          : libelleDocument(o, catalogue);
  const phrases = refus.motifs
    .filter(
      (m, i, tous) => tous.findIndex((x) => x.objet === m.objet && x.nature === m.nature) === i
    )
    .map((m, i) => {
      const nom = objet(m.objet);
      return `${i === 0 ? nom : nom.charAt(0).toLowerCase() + nom.slice(1)} : ${nature(m.nature)}`;
    });
  const commentaire = refus.commentaire.trim().replace(/\s+/g, ' ');
  const fin = commentaire ? ` ${commentaire.charAt(0).toUpperCase()}${commentaire.slice(1)}` : '';
  return `${phrases.join(' ; ')}.${fin && !/[.!?…]$/.test(fin) ? `${fin}.` : fin}`;
}

/** Le statut qu'une affectation donne à une étape pas encore démarrée. */
export function statutApresAffectation(
  statut: StatutEtape,
  affectation: { partenaireId: string | null; debutPrevu: string | null }
): StatutEtape {
  if (statut !== 'a-planifier' && statut !== 'planifiee') return statut;
  return affectation.partenaireId !== null && affectation.debutPrevu !== null
    ? 'planifiee'
    : 'a-planifier';
}

/**
 * Peut-on démarrer l'étape ? La réception du chantier (`finale`) n'a pas de
 * verrou : elle attend que toutes les autres soient validées — sans quoi on la
 * daterait trop tôt. `toutes` : les étapes du chantier, pour le savoir.
 */
export function peutDemarrer(
  etape: Pick<EtapeChantier, 'statut' | 'partenaireId' | 'enAttenteDe'> &
    Partial<Pick<EtapeChantier, 'finale'>>,
  toutes: readonly Soeur[] = []
): Verdict {
  if (etape.partenaireId === null) return { ok: false, raison: 'Affectez d’abord un partenaire.' };
  if (etape.statut !== 'planifiee' && etape.statut !== 'a-planifier') {
    return { ok: false, raison: 'Cette étape est déjà démarrée.' };
  }
  if (etape.enAttenteDe) {
    return {
      ok: false,
      raison: `En attente de : ${etape.enAttenteDe}. Elle doit être validée avant de commencer.`,
    };
  }
  if (etape.finale) {
    const restantes = toutes
      .filter((e) => !e.finale && e.statut !== 'validee')
      .sort((a, b) => a.ordre - b.ordre);
    if (restantes.length > 0) {
      return {
        ok: false,
        raison: `La réception attend la validation de : ${restantes.map((e) => e.libelle.toLowerCase()).join(', ')}.`,
      };
    }
  }
  return { ok: true };
}

/** Pas encore commencée et bloquée par l'étape qu'elle attend : le cadenas de la frise. */
export function estBloquee(etape: Pick<EtapeChantier, 'statut' | 'enAttenteDe'>): boolean {
  return (
    Boolean(etape.enAttenteDe) && (etape.statut === 'a-planifier' || etape.statut === 'planifiee')
  );
}

/**
 * Rouvrir une étape validée. Si la réception était validée, elle se rouvre
 * aussi : un chantier clôturé dont une étape repart n'est plus clôturé.
 */
export function etapesARouvrir(
  etape: Pick<EtapeChantier, 'id' | 'finale' | 'statut'>,
  toutes: readonly Pick<EtapeChantier, 'id' | 'finale' | 'statut'>[]
): string[] {
  if (etape.statut !== 'validee') return [];
  const reception = toutes.find((e) => e.finale);
  if (!etape.finale && reception?.statut === 'validee') return [etape.id, reception.id];
  return [etape.id];
}

/** Une étape se retire d'un chantier tant qu'elle n'a rien : ni validation, ni preuve. */
export function peutRetirer(
  etape: Pick<EtapeChantier, 'finale' | 'statut'> & { nbPreuves: number }
): Verdict {
  if (etape.finale) return { ok: false, raison: 'La réception du chantier ne se retire pas.' };
  if (etape.statut === 'validee') {
    return { ok: false, raison: 'Cette étape est validée : rouvrez-la avant de la retirer.' };
  }
  if (etape.nbPreuves > 0) {
    return { ok: false, raison: 'Cette étape a déjà des preuves : retirez-les avant elle.' };
  }
  return { ok: true };
}

/** La chaîne dans l'ordre, la réception en dernier quoi qu'il arrive. */
export function dansLOrdre<T extends { ordre: number; finale: boolean }>(
  etapes: readonly T[]
): T[] {
  return [...etapes].sort((a, b) => Number(a.finale) - Number(b.finale) || a.ordre - b.ordre);
}

// ── Le chantier vu d'ensemble ────────────────────────────────────────────────

export type StatutChantier = 'a-planifier' | 'en-cours' | 'pret-a-receptionner' | 'cloture';

export const LIBELLE_STATUT_CHANTIER: Record<StatutChantier, string> = {
  'a-planifier': 'À planifier',
  'en-cours': 'En cours',
  'pret-a-receptionner': 'Prêt à réceptionner',
  cloture: 'Clôturé',
};

type EtapeVue = Pick<
  ResumeEtape,
  'finale' | 'statut' | 'debutPrevu' | 'finPrevue' | 'partenaireId' | 'ordre'
>;

export function statutChantier(etapes: readonly EtapeVue[]): StatutChantier {
  const reception = etapes.find((e) => e.finale);
  if (reception?.statut === 'validee') return 'cloture';
  const autres = etapes.filter((e) => !e.finale);
  if (autres.length > 0 && autres.every((e) => e.statut === 'validee'))
    return 'pret-a-receptionner';
  if (etapes.every((e) => e.statut === 'a-planifier')) return 'a-planifier';
  return 'en-cours';
}

// ── Les archives ─────────────────────────────────────────────────────────────

/**
 * Un chantier clôturé depuis plus longtemps ne se charge plus par défaut : les
 * écrans du quotidien n'en ont que faire, et la liste grossit d'année en année.
 */
export const JOURS_AVANT_ARCHIVE = 30;

/** La clôture d'un chantier : la validation de sa réception (db/011-archives.sql). */
export function dateCloture(
  etapes: readonly Pick<EtapeChantier, 'finale' | 'statut' | 'valideeLe'>[]
): string | null {
  const reception = etapes.find((e) => e.finale);
  return reception?.statut === 'validee' ? reception.valideeLe : null;
}

/** L'instant avant lequel une clôture fait du chantier une archive, en ISO. */
export function seuilArchives(maintenant: Date = new Date()): string {
  return new Date(maintenant.getTime() - JOURS_AVANT_ARCHIVE * 86_400_000).toISOString();
}

/* Les dates comparées en instants : la base écrit « +00:00 », le navigateur « Z ». */
export function estArchive(clotureLe: string | null, seuil: string): boolean {
  return clotureLe !== null && Date.parse(clotureLe) < Date.parse(seuil);
}

/** Ce que rend la liste des chantiers : sans les archives, sauf demande expresse. */
export function sansArchives<T extends { clotureLe: string | null }>(
  chantiers: readonly T[],
  avecArchives = false,
  maintenant: Date = new Date()
): T[] {
  if (avecArchives) return [...chantiers];
  const seuil = seuilArchives(maintenant);
  return chantiers.filter((c) => !estArchive(c.clotureLe, seuil));
}

/**
 * Une étape dont la fin prévue est passée sans validation. Une étape envoyée au
 * bureau n'est pas en retard : c'est le bureau qui la fait attendre.
 */
export function enRetard(etape: EtapeVue, jour: string): boolean {
  return (
    etape.statut !== 'validee' &&
    etape.statut !== 'a-valider' &&
    etape.finPrevue !== null &&
    joursEntre(etape.finPrevue, jour) > 0
  );
}

export interface Alerte {
  type: 'rouverte' | 'retard';
  etapeId: string;
}

/** Ce qui demande l'attention du bureau sur un chantier, par gravité. */
export function alertesChantier(
  etapes: readonly (EtapeVue & { id: string })[],
  jour: string
): Alerte[] {
  if (statutChantier(etapes) === 'cloture') return [];
  return [
    ...etapes
      .filter((e) => e.statut === 'rouverte')
      .map((e) => ({ type: 'rouverte' as const, etapeId: e.id })),
    ...etapes
      .filter((e) => enRetard(e, jour))
      .map((e) => ({ type: 'retard' as const, etapeId: e.id })),
  ];
}

/**
 * La prochaine chose à faire : ce qui est à reprendre ou à valider d'abord,
 * l'étape en cours, puis la prochaine planifiée, puis la première à planifier.
 */
export function prochaineEtape<T extends EtapeVue>(etapes: readonly T[]): T | undefined {
  const tri = (a: T, b: T) =>
    (a.debutPrevu ?? '9999').localeCompare(b.debutPrevu ?? '9999') || a.ordre - b.ordre;
  const parStatut = (s: StatutEtape) => etapes.filter((e) => e.statut === s).sort(tri)[0];
  return (
    parStatut('rouverte') ??
    parStatut('a-valider') ??
    parStatut('en-cours') ??
    parStatut('planifiee') ??
    dansLOrdre(etapes.filter((e) => e.statut === 'a-planifier'))[0]
  );
}

/** « CH-2026-014 » : l'année, puis un numéro sur trois chiffres qui suit le plus grand existant. */
export function prochaineReference(existantes: readonly string[], annee: number): string {
  const prefixe = `CH-${annee}-`;
  const max = existantes
    .filter((r) => r.startsWith(prefixe))
    .map((r) => Number(r.slice(prefixe.length)))
    .filter((n) => Number.isFinite(n))
    .reduce((a, b) => Math.max(a, b), 0);
  return `${prefixe}${String(max + 1).padStart(3, '0')}`;
}

/** Un identifiant de document à partir de son nom : « Plan d’architecte » → « plan-d-architecte ». */
export function identifiantDocument(libelle: string, existants: readonly string[]): string {
  const base =
    libelle
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 50) || 'document';
  let id = base;
  for (let n = 2; existants.includes(id) || id === 'photo' || id === 'autre'; n++)
    id = `${base}-${n}`;
  return id;
}
